import { FILENAME_TO_SHORTNAME_MAP } from './constants';

export const formatS3UriToFilename = (s3UriRaw: string): string => {
  // Clean up S3 URI for display
  const s3Uri = s3UriRaw.replace('s3://', '').replace(/\/+/g, '/');
  // Extract the filename from the S3 URI
  const parts = s3Uri.split('/');
  return parts[parts.length - 1];
};

const FILENAME_DELIMITERS_REGEX = /[_-]/g;
/**
 * Normalize filename delimiters to underscores
 * @param filename
 * @returns
 */
const normalizeFilename = (filename: string): string => {
  return filename.replace(FILENAME_DELIMITERS_REGEX, '_');
};

export const getCitationTitle = (filename: string): string => {
  return (
    FILENAME_TO_SHORTNAME_MAP[normalizeFilename(filename)] ||
    'Unknown shortname'
  );
};

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Get the start of the week (Monday) for a given date in UTC
 * @param date The reference date
 * @returns ISO date string for the Monday of that week (YYYY-MM-DD) in UTC
 */
export const getWeekStart = (date: Date): string => {
  const d = new Date(date);
  const day = d.getUTCDay(); // Use UTC day
  // For Monday start: Sunday = 6 days back, Monday = 0 days back, Tuesday = 1 day back, etc.
  const daysToSubtract = day === 0 ? 6 : day - 1;
  d.setUTCDate(d.getUTCDate() - daysToSubtract);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

/**
 * Get the end of the week (Sunday) for a given week start date
 * @param weekStart ISO date string for the Monday start (YYYY-MM-DD)
 * @returns ISO date string for the Sunday end (YYYY-MM-DD)
 */
export const getWeekEnd = (weekStart: string): string => {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end.toISOString().split('T')[0];
};

/**
 * Get the last N complete calendar weeks (Monday to Sunday)
 * Excludes the current incomplete week
 * @param n Number of complete weeks to return
 * @param referenceDate Optional reference date (defaults to current date)
 * @returns Array of ISO date strings for week starts (YYYY-MM-DD), ordered chronologically
 */
export const getLastNCompleteWeeks = (
  n: number,
  referenceDate: Date = new Date(),
): string[] => {
  const weeks: string[] = [];

  // Find the start of the current week (Monday) in UTC
  const currentWeekStart = new Date(referenceDate);
  const currentDay = currentWeekStart.getUTCDay();
  // For Monday start: Sunday = 6 days back, Monday = 0 days back, Tuesday = 1 day back, etc.
  const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1;
  currentWeekStart.setUTCDate(referenceDate.getUTCDate() - daysToSubtract);
  currentWeekStart.setUTCHours(0, 0, 0, 0);

  // Generate the last N complete weeks (not including current incomplete week)
  for (let i = 1; i <= n; i++) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setUTCDate(currentWeekStart.getUTCDate() - i * 7);
    weeks.unshift(weekStart.toISOString().split('T')[0]);
  }

  return weeks;
};

/**
 * Get the current week boundaries (for display purposes)
 * @param referenceDate Optional reference date (defaults to current date)
 * @returns Object with start and end dates of the current week
 */
export const getCurrentWeekBoundaries = (referenceDate: Date = new Date()) => {
  const weekStartStr = getWeekStart(referenceDate);
  const weekEndStr = getWeekEnd(weekStartStr);

  return {
    start: weekStartStr,
    end: weekEndStr,
    startDate: new Date(weekStartStr + 'T00:00:00Z'),
    endDate: new Date(weekEndStr + 'T23:59:59Z'),
  };
};
